import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PagePayload } from '@core/interface/core';
import {
  AddEmployeeResponse,
  ChangePasswordPayload,
  Employee,
  EmployeeByRoleResponse,
  EmployeeCommonResponse,
  EmployeeProfileResposne,
  EmployeeResponse,
  TypeData,
  TypeResponse,
  TypeUpdateResponse,
} from '@core/interface/employee';
import {
  AddDocumentPayload,
  AddDocumentResponse,
  ChangeCompanyPayload,
  DocumentCommonResponse,
  DocumentItem,
  DocumentResponse,
  EditDocumentResponse,
} from '@core/interface/employee-document';
import { environment } from '@environment/environment';

@Injectable({
  providedIn: 'root',
})
export class EmployeeService {
  constructor(private http: HttpClient) {}

  getEmployees(filter?: PagePayload) {
    let httpParams = new HttpParams();
    if (filter)
      Object.keys(filter).forEach((key) => {
        httpParams = httpParams.set(
          key,
          (filter[key as keyof PagePayload] as any) || ''
        );
      });
    return this.http.get<EmployeeResponse>(environment.endPoints.employeeList, {
      params: httpParams,
    });
  }

  addEmployee(data: Employee) {
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (key === 'attachments') {
        const attachments = data[key as keyof Employee] as any[];
        attachments.forEach((att, i) => {
          Object.keys(att).forEach((attKey) => {
            formData.append(`attachments[${i}][${attKey}]`, att[attKey] || '');
          });
        });
      } else {
        const value = data[key as keyof Employee] as string | Blob;
        formData.append(key, value ?? '');
      }
    });
    return this.http.post<AddEmployeeResponse>(
      environment.endPoints.employeeAddEdit,
      formData
    );
  }
  deleteEmployee(id: number) {
    return this.http.get<EmployeeCommonResponse>(
      environment.endPoints.employeeDelete(id)
    );
  }

  getEmployeeByRole(role_id: number, master_company_id?: string | number) {
    let httpParams = new HttpParams().set('role_id', role_id);
    if (master_company_id) {
      httpParams = httpParams.set('master_company_id', master_company_id);
    }
    return this.http.get<EmployeeByRoleResponse>(
      environment.endPoints.employeeWithRole,
      {
        params: httpParams,
      }
    );
  }

  logout() {
    return this.http.get<EmployeeCommonResponse>(
      environment.endPoints.employeeLogout
    );
  }

  profile() {
    return this.http.get<EmployeeProfileResposne>(
      environment.endPoints.employeeProfile
    );
  }

  chnagePassword(data: ChangePasswordPayload) {
    return this.http.post<EmployeeCommonResponse>(
      environment.endPoints.changePassword,
      data
    );
  }

  getEmploymentTypes() {
    return this.http.get<TypeResponse>(
      environment.endPoints.employmentTypeList
    );
  }

  addEmploymentType(data: TypeData) {
    return this.http.post<TypeUpdateResponse>(
      environment.endPoints.employmentTypeAddEdit,
      data
    );
  }

  getWorkSchedules() {
    return this.http.get<TypeResponse>(environment.endPoints.workScheduleList);
  }

  addWorkSchedule(data: TypeData) {
    return this.http.post<TypeUpdateResponse>(
      environment.endPoints.workScheduleAddEdit,
      data
    );
  }

  getWorkLocations() {
    return this.http.get<TypeResponse>(environment.endPoints.workLocationList);
  }

  addWorkLocation(data: TypeData) {
    return this.http.post<TypeUpdateResponse>(
      environment.endPoints.workLocationAddEdit,
      data
    );
  }

  deleteAttachment(id: number) {
    return this.http.get<EmployeeCommonResponse>(
      environment.endPoints.employeeAttachmentDelete(id)
    );
  }

  getDocuments(filter?: PagePayload) {
    let httpParams = new HttpParams();
    if (filter)
      Object.keys(filter).forEach((key) => {
        httpParams = httpParams.set(
          key,
          (filter[key as keyof PagePayload] as any) || ''
        );
      });
    return this.http.get<DocumentResponse>(
      environment.endPoints.employeeDocumentList,
      {
        params: httpParams,
      }
    );
  }

  addDocument(data: AddDocumentPayload) {
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (key === 'attachments') {
        (data.attachments || []).forEach((att, i) => {
          formData.append(`attachments[${i}]`, att);
        });
      } else {
        const value = data[key as keyof AddDocumentPayload] as string | Blob;
        formData.append(key, value ?? '');
      }
    });
    return this.http.post<AddDocumentResponse>(
      environment.endPoints.employeeAddDocument,
      formData
    );
  }
  deleteDocument(id: number) {
    return this.http.get<DocumentCommonResponse>(
      environment.endPoints.employeeDocumentDelete(id)
    );
  }

  editDocument(data: AddDocumentPayload) {
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (key === 'attachments') {
        (data.attachments || []).forEach((att, i) => {
          formData.append(`attachments[${i}]`, att);
        });
      } else {
        const value = data[key as keyof AddDocumentPayload] as string | Blob;
        formData.append(key, value ?? '');
      }
    });
    return this.http.post<EditDocumentResponse>(
      environment.endPoints.employeeEditDocument,
      formData
    );
  }

  changeCompany(data: ChangeCompanyPayload) {
    return this.http.post<EmployeeCommonResponse>(
      environment.endPoints.changeCompany,
      data
    );
  }
}
